import { useState } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedVisionUser,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import {
  calculateActions,
  defaultActions,
  endIncidentDestination,
  getLatestPlaceOfSafety,
  getLatestPolicePresence,
  IncidentWorkItemIdSchema,
} from "@thalamos/vision-common";

import { apiHooks } from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import { ReviewBeforeEndingModal } from "../../modals/ReviewBeforeEnding/ReviewBeforeEnding.js";
import { Error as ErrorComponent } from "../../pages/Error/Error.js";
import { createRoute } from "../../routing/createRoute.js";
import { endIncidentRoute } from "../../routing/endIncidentRoute.js";

type PolicePresenceWhatWillYouDoPageProps = Record<string, never>;

// TODO: this is a placehold page for someone else to build out properly
function PolicePresenceWhatWillYouDoPageInner(props: {
  incidentWorkItem: IncidentWorkItemWithContext;
  user: ExtendedVisionUser | undefined;
}) {
  const { user, incidentWorkItem } = props;

  const navigate = useNavigate();

  const actions = user
    ? calculateActions({ incidentWorkItem, user, asOfDateTime: dayjs() })
    : defaultActions;

  const [isReviewBeforeEndingModalOpen, setIsReviewBeforeEndingModalOpen] =
    useState(false);

  const latestPlaceOfSafety = getLatestPlaceOfSafety(incidentWorkItem);
  const latestPolicePresenceRequest = getLatestPolicePresence(incidentWorkItem);

  return (
    <Paper
      sx={{
        display: "flex",
        justifyContent: "center",
        flexGrow: 1,
        padding: 2,
      }}
    >
      <Stack
        sx={{
          gap: 3,
        }}
      >
        <Stack>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={() => {
                window.history.back();
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography>Police Presence Input</Typography>
          </Box>
        </Stack>
        <Divider />
        <Typography
          sx={{
            fontWeight: "bold",
          }}
        >
          How do you want to proceed?
        </Typography>
        <Stack>
          <Button
            variant="outlined"
            sx={{ padding: 2 }}
            onClick={() => {
              navigate(
                createRoute.incidentReviewPlacesOfSafetyPage(
                  incidentWorkItem.incidentWorkItem.id,
                ),
              );
            }}
          >
            Change POS
          </Button>
          <Typography>
            If you are still with the patient but have changed location.
          </Typography>
        </Stack>
        <Divider />
        <Stack>
          <Button
            variant="outlined"
            sx={{ padding: 2 }}
            color={"success"}
            onClick={() => {
              setIsReviewBeforeEndingModalOpen(true);
            }}
          >
            Finish Form
          </Button>
          <Typography>
            If you have left the patient and the Place of Safety.
          </Typography>
        </Stack>
      </Stack>
      {isReviewBeforeEndingModalOpen && (
        <ReviewBeforeEndingModal
          open={isReviewBeforeEndingModalOpen}
          navigate={navigate}
          onClose={() => {
            setIsReviewBeforeEndingModalOpen(false);
          }}
          canEnterOfficerRequestInformation={
            actions.policePresence.canEnterOfficerRequestInformation
          }
          latestPlaceOfSafetyName={latestPlaceOfSafety?.name}
          latestPolicePresenceName={
            latestPolicePresenceRequest?.hasRemainedAtPlaceOfSafety
          }
          latestPolicePresenceUpdatedAt={
            latestPolicePresenceRequest?.createdAt
              ? latestPolicePresenceRequest.createdAt.toDate()
              : undefined
          }
          incidentWorkItem={incidentWorkItem}
          onContinue={() => {
            setIsReviewBeforeEndingModalOpen(false);
            navigate(
              endIncidentRoute(
                incidentWorkItem.incidentWorkItem.id,
                endIncidentDestination(actions.endIncident),
              ),
            );
          }}
        />
      )}
    </Paper>
  );
}

export function PolicePresenceWhatWillYouDoPage(
  _props: PolicePresenceWhatWillYouDoPageProps,
) {
  const params = useParams();
  const { user } = useUser();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    {
      data: incidentWorkItem,
      loading: isIncidentLoading,
      error: incidentError,
    },
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  if (incidentError) {
    return <ErrorComponent />;
  }

  if (isIncidentLoading || incidentWorkItem === undefined) {
    return <CircularProgress />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        margin: ["1rem", "1rem", "10rem"],
      }}
    >
      <PolicePresenceWhatWillYouDoPageInner
        incidentWorkItem={incidentWorkItem}
        user={user}
      />
    </Box>
  );
}
